import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, Fetcher, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { seoList, seoCreate, seoDelete, SEO_STR as S, type SeoConfigRow } from './seoApi'

/**
 * Umumiy SEO config-CRUD jadvali. A12 ning barcha "ro'yxat + qo'shish-form +
 * o'chirish" konfiguratsion ekranlari (meta / OG / canonical / index-policy /
 * noindex / schema / hreflang / sitemap / landing / blog / mini-app / deep-link)
 * shu bitta komponent orqali quriladi. Backend `/admin/seo/*` greenfield →
 * `seoList` fail-soft bo'sh qaytaradi; mutatsiya POST/DELETE qiladi. Placeholder
 * huquq `USERS_VIEW` (REGISTER.md dagi `SEO_*` huquqlari kelguncha).
 */
export interface SeoField {
  /** Yuboriladigan `body` kaliti. */
  name: string
  placeholder: string
  mono?: boolean
  /** Berilsa — <select> (bo'sh birinchi variant = placeholder). */
  options?: { value: string; label: string }[]
}

export interface SeoConfigTableProps {
  screenId: string
  title: string
  addLabel: string
  /** Backend yo'li, masalan `/admin/seo/meta-templates`. */
  path: string
  /** Ro'yxat javobidagi massiv kaliti, masalan `templates` / `entries`. */
  listKey: string
  /** Tez qidiruvda solishtiriladigan maydonlar. */
  searchKeys: string[]
  fields: SeoField[]
  columns: AdminColumn<SeoConfigRow>[]
  icon: ReactNode
  headerActions?: ReactNode
}

export function SeoConfigTable({
  screenId,
  title,
  addLabel,
  path,
  listKey,
  searchKeys,
  fields,
  columns,
  icon,
  headerActions,
}: SeoConfigTableProps) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)

  const firstName = fields[0]?.name ?? ''
  const canAdd = Boolean(form[firstName]?.trim())

  const add = async () => {
    if (!canAdd) return
    setBusy(true)
    try {
      const body: Record<string, string> = {}
      for (const f of fields) body[f.name] = (form[f.name] ?? '').trim()
      await seoCreate(path, body)
      toast(S.createdGeneric, { tone: 'success' })
      setForm({})
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<SeoConfigRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.delete,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await seoDelete(path, r.id)
            toast(S.deletedGeneric, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [path, toast],
  )

  // DataTable `fetcher` ni barqaror identity bilan kutadi (index.tsx effekti).
  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<SeoConfigRow>>(
    () => seoList<SeoConfigRow>(path, listKey, searchKeyStr.split('|')),
    [path, listKey, searchKeyStr],
  )

  return (
    <>
      <PageHeader title={title} screenId={screenId} actions={headerActions} />
      <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
        {fields.map((f) =>
          f.options ? (
            <select
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
            >
              <option value="">{f.placeholder}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={f.name}
              value={form[f.name] ?? ''}
              onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
              placeholder={f.placeholder}
              spellCheck={false}
              className={`min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong ${
                f.mono ? 'font-mono' : ''
              }`}
            />
          ),
        )}
        <button
          type="button"
          onClick={add}
          disabled={busy || !canAdd}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {addLabel}
        </button>
      </div>
      <DataTable<SeoConfigRow>
        key={reloadKey}
        screenId={screenId}
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />
    </>
  )
}
